import styled from '@emotion/styled';
import { useEffect, useRef, useState } from 'react';
import { useCalibration } from '../../../context/calibrationContext';
import { useTestContext } from '../../../context/testContext';
import { usePostMessage, useReceiveMessage } from '../../../context/messageContext';
import { useSessionState } from '../../../context/sessionStateContext';
import { colors, fonts, breakpoints, RadioGroup, StatusPill, IconButton, RefreshIcon, CheckCircleIcon, AlertIcon } from '../../../ui';

// "Automatic Resizing Mode (•) On ( ) Off" + "Patient Distance [500mm] [⟳] ✓ Patient Positioned"
// (Figma scoring steps). Same behaviour as components/AcuityInfoPanel + PatientDistanceIndicator:
// the mode lives in testData.testMode ('auto' | 'off'); 'auto' asks the patient app to recalculate
// the distance; patientMovementDetected messages drive the status pill. The mode and the collected
// distance are also written to the shared session state (auto_resize, distance.mm).

const Wrap = styled.div`
  display: grid;
  grid-template-columns: ${({ inline }) => (inline ? 'minmax(0, auto) minmax(0, 1fr)' : 'minmax(0, 1fr)')};
  gap: 16px 32px;
  align-items: end;
  width: 100%;
  font-family: ${fonts.family};
  @media (max-width: ${breakpoints.phone - 1}px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 12px;
  }
`;

const Label = styled.span`
  display: block;
  margin-bottom: 6px;
  font-size: 12px;
  line-height: 20px;
  font-weight: 700;
  color: ${colors.slate[700]};
`;

const DistanceRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
`;

const DistanceField = styled.div`
  display: flex;
  align-items: stretch;
  height: 40px;
  .value {
    display: flex;
    align-items: center;
    min-width: 88px;
    padding: 0 12px;
    border: 1px solid ${colors.slate[200]};
    border-right: 0;
    border-radius: 5px 0 0 5px;
    background: ${colors.slate[100]};
    color: ${colors.slate[500]};
    font-size: 15px;
    font-variant-numeric: tabular-nums;
    box-sizing: border-box;
  }
  button {
    height: 40px;
    width: 40px;
    border-radius: 0 5px 5px 0;
  }
`;

const pillFor = (state) =>
  ({
    positioned: { tone: 'success', icon: <CheckCircleIcon size={14} />, text: 'Patient Positioned' },
    detecting: { tone: 'warning', icon: <AlertIcon size={14} />, text: 'Detecting distance…' },
    moving: { tone: 'error', icon: <AlertIcon size={14} />, text: 'Patient moving' },
  })[state];

/** Presentational version (dev showcase). */
export const AutoResizeView = ({ mode, onMode, modeDisabled, distanceMm, onRefresh, refreshDisabled, status = 'positioned', inline }) => {
  const pill = pillFor(status);
  return (
    <Wrap inline={inline} data-testid='auto-resize-controls'>
      <div>
        <Label>Automatic Resizing Mode</Label>
        <RadioGroup
          ariaLabel='Automatic Resizing Mode'
          options={[
            { value: 'auto', label: 'On' },
            { value: 'off', label: 'Off' },
          ]}
          value={mode}
          onChange={onMode}
          disabled={modeDisabled}
        />
      </div>
      <div>
        <Label>Patient Distance</Label>
        <DistanceRow>
          <DistanceField>
            <span className='value' aria-label={`Patient distance ${distanceMm ? `${distanceMm} millimetres` : 'unknown'}`}>{`${distanceMm || '--'}mm`}</span>
            <IconButton label='Recalculate the patient distance' variant='outline' size={40} onClick={onRefresh} disabled={refreshDisabled}>
              <RefreshIcon size={18} />
            </IconButton>
          </DistanceField>
          <StatusPill tone={pill.tone} role='status'>
            {pill.icon}
            {pill.text}
          </StatusPill>
        </DistanceRow>
      </div>
    </Wrap>
  );
};

const AutoResizeControls = ({ inline }) => {
  const { testData, updateTestData } = useTestContext();
  const [patientMoving, setPatientMoving] = useState(testData.testMode === 'auto');
  const [status, setStatus] = useState(testData.testMode === 'auto' ? 'detecting' : 'positioned');
  const postMessage = usePostMessage();
  const receivedMessage = useReceiveMessage();
  const { patch } = useSessionState();
  const {
    calibrationData: { methodSelected, distanceFromScreen },
  } = useCalibration();
  const lastDistance = useRef(null);

  useEffect(() => {
    if (testData.testMode === 'auto') postMessage({ type: 'recalculateDistance' });
  }, [testData.testMode, postMessage]);

  useEffect(() => {
    patch({ auto_resize: testData.testMode === 'auto' }).catch(() => {});
  }, [testData.testMode, patch]);

  useEffect(() => {
    if (receivedMessage?.type !== 'patientMovementDetected') return;
    const { patientIsMoving, patientDistance } = receivedMessage?.content;
    setPatientMoving(patientIsMoving);
    if (patientIsMoving && !patientDistance) return setStatus('detecting');
    if (patientIsMoving) return setStatus('moving');
    return setStatus('positioned');
  }, [receivedMessage]);

  const mm = distanceFromScreen ? Math.round(distanceFromScreen) : null;
  useEffect(() => {
    if (!mm || patientMoving) return;
    if (lastDistance.current !== null && Math.abs(lastDistance.current - mm) < 5) return;
    lastDistance.current = mm;
    patch({ distance: { mm, positioned: true } }).catch(() => {});
  }, [mm, patientMoving, patch]);

  const autoResizingDisabled = patientMoving || methodSelected === 'manual';

  return (
    <AutoResizeView
      inline={inline}
      mode={testData.testMode}
      onMode={(value) => updateTestData({ testMode: value })}
      modeDisabled={autoResizingDisabled}
      distanceMm={mm}
      onRefresh={() => testData.testMode === 'auto' && postMessage({ type: 'recalculateDistance' })}
      refreshDisabled={testData.testMode !== 'auto' || patientMoving}
      status={status}
    />
  );
};

export default AutoResizeControls;
